import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FaThumbtack } from "react-icons/fa";
import { formatDate, isNew } from "../../content/newsletters/format";
import {
  categoryAccent,
  categoryLabel,
  getCategory,
} from "../../content/newsletters/categories";
import { Dot } from "./CategoryNav";

const PINNED_LABEL = { ko: "고정", en: "Pinned" };

/**
 * 목록의 한 항목. 카드가 아니라 구분선으로 나눈 목록 행이다 —
 * 같은 높이의 상자를 늘어놓는 것보다 제목이 먼저 읽힌다.
 *
 * 카테고리 하나만 보고 있을 때는 행마다 카테고리를 다시 적지 않는다(showCategory=false).
 * 행 전체가 링크라 카테고리 표기는 링크가 아니라 글자다 — 링크 안에 링크를 둘 수 없다.
 *
 * 썸네일이 있으면 글 오른쪽에 작게 붙인다. 제목보다 앞에 오지 않게 해서
 * 목록을 훑을 때는 여전히 글자가 먼저 읽힌다. 썸네일이 없는 글은 예전처럼 글자만 그린다.
 */
function PostLink({ post, lang, showCategory = true }) {
  const category = showCategory ? getCategory(post.category) : null;
  const thumbnail = post.thumbnail;

  return (
    <Row to={`/${lang}/newsletter/${post.slug}`} $hasThumb={Boolean(thumbnail)}>
      <Text>
        <Head>
          {post.pinned ? (
            <Pinned>
              <PinIcon aria-hidden="true" />
              {PINNED_LABEL[lang] || PINNED_LABEL.ko}
            </Pinned>
          ) : null}
          {isNew(post.date) ? <New>NEW</New> : null}
          {category ? (
            <Category style={{ "--nl-tab-accent": categoryAccent(category) }}>
              <Dot aria-hidden="true" />
              {categoryLabel(category, lang)}
            </Category>
          ) : null}
          <time dateTime={post.date}>{formatDate(post.date, lang)}</time>
          {post.tags && post.tags.length > 0 ? (
            <span>{post.tags.join(", ")}</span>
          ) : null}
        </Head>
        <Title>{post.title}</Title>
        {post.summary ? <Summary>{post.summary}</Summary> : null}
      </Text>
      {/* 제목이 이미 링크 이름이므로 이미지는 장식으로 둔다. */}
      {thumbnail ? (
        <Thumb src={thumbnail} alt="" loading="lazy" decoding="async" />
      ) : null}
    </Row>
  );
}

const Title = styled.h2`
  font-size: 1.3125rem;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.02em;
  color: var(--nl-ink);
  transition: color 0.18s ease;

  @media screen and (max-width: 768px) {
    font-size: 1.1875rem;
  }
`;

const Row = styled(Link)`
  display: grid;
  grid-template-columns: ${({ $hasThumb }) => ($hasThumb ? "1fr 10rem" : "1fr")};
  gap: 1.75rem;
  align-items: start;
  padding: 2rem 0;
  border-bottom: 1px solid var(--nl-line);
  text-decoration: none;

  &:first-child {
    border-top: 1px solid var(--nl-line);
  }

  &:hover ${Title} {
    color: var(--nl-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--nl-accent);
    outline-offset: 4px;
    border-radius: 2px;
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: ${({ $hasThumb }) =>
      $hasThumb ? "1fr 6.5rem" : "1fr"};
    gap: 1rem;
  }
`;

const Text = styled.div`
  min-width: 0;
`;

const Thumb = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  /* 이미지를 받는 동안에도 자리가 보이도록 */
  background: var(--nl-surface);
  border-radius: 6px;
  /* 윗변을 날짜 글자의 윗선에 맞춘다 */
  margin-top: 0.25rem;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nl-ink-mute);
  font-variant-numeric: tabular-nums;
`;

const Category = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 600;
  color: var(--nl-ink-soft);
`;

// 강조색(--nl-accent)은 작은 글자에서 대비가 모자라 잉크색 + 굵기로만 구분한다.
const Pinned = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  font-weight: 700;
  color: var(--nl-ink);
`;

/* 게시판에 꽂힌 압정처럼 살짝 기울인다. 글자와 같은 잉크색이라 대비 걱정이 없다. */
const PinIcon = styled(FaThumbtack)`
  flex: none;
  width: 0.75rem;
  height: 0.75rem;
  transform: rotate(35deg);
`;

// 브랜드 색 바탕 위 잉크 글자는 5.1:1 — 작은 글자도 AA를 넘긴다. (흰 글자는 3.4:1 이라 쓰지 않는다)
// --nl-accent 가 아니라 --nl-brand 를 쓴다. 게임 카테고리 화면에서는 --nl-accent 가 진한
// 게임 색으로 바뀌어 그 위의 잉크 글자가 읽히지 않는다.
const New = styled.span`
  padding: 0.125rem 0.4375rem;
  border-radius: 4px;
  background: var(--nl-brand);
  color: var(--nl-ink);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.06em;
`;

const Summary = styled.p`
  margin-top: 0.625rem;
  font-size: 0.9375rem;
  line-height: 1.75;
  color: var(--nl-ink-soft);
`;

export default PostLink;
